import { Plugin } from 'tiptap'
import { Image } from 'tiptap-extensions'
import { buildImageUrlFromRefFileName } from '@data/attachment'
import ImageView from './image/image-view.vue'

export const DEFAULT_IMAGE_URL_REGEX =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/
export const DEFAULT_IMAGE_WIDTH = 200
export const DEFAULT_IMAGE_DISPLAY = 'inline'

// @ts-ignore
function getAttrs(dom) {
  const { cssFloat, display } = dom.style
  let { width, height } = dom.style

  let dp = dom.getAttribute('data-display') || dom.getAttribute('display')
  if (dp) {
    dp = /(inline|block|left|right)/.test(dp) ? dp : 'inline'
  } else if (cssFloat === 'left' && !display) {
    dp = 'left'
  } else if (cssFloat === 'right' && !display) {
    dp = 'right'
  } else if (!cssFloat && display === 'block') {
    dp = 'block'
  } else {
    dp = 'inline'
  }

  width = dom.getAttribute('width') || width || null
  height = dom.getAttribute('height') || width || null

  return {
    src: dom.getAttribute('src') || '',
    title: dom.getAttribute('title') || '',
    alt: dom.getAttribute('alt') || '',
    width: width == null ? null : parseInt(width, 10),
    height: height == null ? null : parseInt(height, 10),
    display: dp,
  }
}

function toDOM(node) {
  const { src, alt, title, width, height, display } = node.attrs

  const attrs = {
    src,
    alt,
    title,
    width,
    height,
  }

  attrs['data-display'] = display

  return ['img', attrs]
}

/**
 * Plugin for the tiptap editor that adds images to the editor
 * @see https://github.com/scrumpy/tiptap/blob/0f17abeee6df1a8b40c6c96413a158918ec45d34/packages/tiptap-extensions/src/nodes/Image.js
 * This class overwrites the default `image`. You need to make sure to **not** use the original class.
 */
export default class ImageUpload extends Image {
  constructor(options = {}) {
    super(options)
    this.uploader = options.uploader
  }

  get schema() {
    return {
      inline: true,
      attrs: {
        src: {
          default: '',
        },
        alt: {
          default: '',
        },
        title: {
          default: '',
        },
        width: {
          default:
            this.imageDefaultWidth > 0
              ? this.imageDefaultWidth
              : DEFAULT_IMAGE_WIDTH,
        },
        height: {
          default: null,
        },
        display: {
          default: /(inline|block|left|right)/.test(this.defaultDisplay)
            ? this.defaultDisplay
            : DEFAULT_IMAGE_DISPLAY,
        },
      },
      group: 'inline',
      draggable: true,
      parseDOM: [{ tag: 'img[src]', getAttrs }],
      toDOM,
    }
  }

  get view() {
    return ImageView
  }

  get name() {
    return 'image'
  }
  get plugins() {
    const uploader = this.uploader
    return [
      new Plugin({
        props: {
          handlePaste(view, event, slice) {
            const items = (
              event.clipboardData || event.originalEvent.clipboardData
            ).items
            if (items.length === 1 && items[0].type.indexOf('image') >= 0) {
              const item = items[0]
              event.preventDefault()
              const { schema } = view.state

              const image = item.getAsFile()

              if (uploader) {
                uploader(image).then((file) => {
                  const node = schema.nodes.image.create({
                    src: buildImageUrlFromRefFileName(file.refFileName),
                  })
                  const transaction = view.state.tr.replaceSelectionWith(node)
                  view.dispatch(transaction)
                })
              } else {
                const reader = new FileReader()
                reader.onload = (readerEvent) => {
                  const node = schema.nodes.image.create({
                    src: readerEvent.target.result,
                  })
                  const transaction = view.state.tr.replaceSelectionWith(node)
                  view.dispatch(transaction)
                }
                reader.readAsDataURL(image)
              }
              return false
            }
          },
          handleDOMEvents: {
            drop: function drop(view, event) {
              var hasFiles =
                event.dataTransfer &&
                event.dataTransfer.files &&
                event.dataTransfer.files.length

              if (!hasFiles) {
                return
              }

              var images = Array.from(event.dataTransfer.files).filter(
                function (file) {
                  return /image/i.test(file.type)
                }
              )

              if (images.length === 0) {
                return
              }

              event.preventDefault()
              var schema = view.state.schema
              var coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              })
              images.forEach(function (image) {
                if (!uploader) {
                  return
                }
                uploader(image).then((file) => {
                  var reader = new FileReader()

                  reader.onload = function (readerEvent) {
                    var node = schema.nodes.image.create({
                      src: buildImageUrlFromRefFileName(file.refFileName),
                      // src: readerEvent.target.result,
                    })
                    var transaction = view.state.tr.insert(
                      coordinates.pos,
                      node
                    )
                    view.dispatch(transaction)
                  }

                  reader.readAsDataURL(image)
                })
              })
            },
          },
        },
      }),
    ]
  }
}
